import { useEffect, useRef, useState } from "react";
import {
  EMPTY_EVENT,
  EMPTY_SCHEDULE,
  EVENT_FIELDS,
  SCHEDULE_FIELDS,
  addDays,
  getEventFieldError,
  getScheduleFieldError,
  normalizeSchedule,
} from "./eventFromUtils";

export const useEventForm = ({ blankSchedule = false } = {}) => {
  const [eventData, setEventData] = useState(EMPTY_EVENT);
  const [eventTouched, setEventTouched] = useState({});
  const [scheduleData, setScheduleData] = useState(blankSchedule ? [{ ...EMPTY_SCHEDULE }] : []);

  // Kept in the same order as scheduleData, one object per row
  const [scheduleTouched, setScheduleTouched] = useState(blankSchedule ? [{}] : []);

  const handleEventChange = (e) => {
    const { name, value } = e.target;
    setEventData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "event_start_date" && value) {
        if (!prev.event_end_date) {
          // end date empty -> start date + 1 day
          next.event_end_date = addDays(value, 1);
        } else if (value > prev.event_end_date) {
          // start date is after the end date -> clear the end date
          next.event_end_date = "";
        }
      }

      return next;
    });
  };

  const handleEventBlur = (name) => setEventTouched((prev) => ({ ...prev, [name]: true }));

  const loadSchedules = (list) => {
    setScheduleData(list.map(normalizeSchedule));
    setScheduleTouched(list.map(() => ({})));
  };

  const handleScheduleChange = (e, index) => {
    const { name, value } = e.target;

    setScheduleData((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [name]: value } : row))
    );
  };

  const handleScheduleBlur = (name, index) =>
    setScheduleTouched((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [name]: true } : row))
    );

  const addSchedule = () => {
    setScheduleData((prev) => [...prev, { ...EMPTY_SCHEDULE }]);
    setScheduleTouched((prev) => [...prev, {}]);
  };

  const deleteSchedule = (index) => {
    setScheduleData((prev) => prev.filter((_, i) => i !== index));
    setScheduleTouched((prev) => prev.filter((_, i) => i !== index));
  };
  // Moves one schedule (and its touched flags) from position `from` to position `to`
  const reorderSchedule = (from, to) => {
    const move = (list) => {
      const next = [...list];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    };

    setScheduleData(move);
    setScheduleTouched(move);
  };

  const getEventError = (field) => getEventFieldError(field, eventData);

  const getVisibleEventError = (field) => (eventTouched[field.name] ? getEventError(field) : "");

  const getScheduleError = (field, index) =>
    getScheduleFieldError(field, scheduleData[index], eventData);

  const getVisibleScheduleError = (field, index) =>
    scheduleTouched[index]?.[field.name] ? getScheduleError(field, index) : "";

  const touchAllFields = () => {
    const touchedEventFields = {};

    EVENT_FIELDS.forEach((field) => {
      touchedEventFields[field.name] = true;
    });

    const touchedScheduleFields = scheduleData.map(() => {
      const touchedFields = {};

      SCHEDULE_FIELDS.forEach((field) => {
        touchedFields[field.name] = true;
      });

      return touchedFields;
    });

    setEventTouched(touchedEventFields);
    setScheduleTouched(touchedScheduleFields);
  };

  const isFormValid =
    EVENT_FIELDS.every((field) => !getEventError(field)) &&
    scheduleData.every((_, index) =>
      SCHEDULE_FIELDS.every((field) => !getScheduleError(field, index))
    );

  return {
    eventData,
    setEventData,
    scheduleData,
    loadSchedules,
    handleEventChange,
    handleEventBlur,
    handleScheduleChange,
    handleScheduleBlur,
    addSchedule,
    deleteSchedule,
    reorderSchedule,
    getVisibleEventError,
    getVisibleScheduleError,
    isFormValid,
    touchAllFields,
  };
};

export const useNewFiles = () => {
  const [newFiles, setNewFiles] = useState([]);
  const latestFiles = useRef(newFiles);
  latestFiles.current = newFiles;

  useEffect(
    () => () => latestFiles.current.forEach((item) => URL.revokeObjectURL(item.preview)),
    []
  );

  const addFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;

    setNewFiles((prev) => [
      ...prev,
      ...selected.map((file) => ({
        id: Math.random().toString(36).substring(2, 9),
        file,
        preview: URL.createObjectURL(file),
      })),
    ]);

    e.target.value = "";
  };

  const removeFile = (fileId) =>
    setNewFiles((prev) => {
      const target = prev.find((item) => item.id === fileId);

      if (target) {
        URL.revokeObjectURL(target.preview);
      }

      return prev.filter((item) => item.id !== fileId);
    });

  return {
    newFiles,
    addFiles,
    removeFile,
  };
};
